package com.arena_web.ui.components.chat

import androidx.compose.runtime.Composable
import com.arena_web.data.dto.ChatMemberDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodyXlMedium
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

private val FieldBackground =
    "${AppGradients.InteractiveSecondary} padding-box, " +
        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
        "${AppGradients.ButtonBorderGradientPrimary} border-box"

/**
 * Remove-member confirmation body (Figma 8636:426640) — the member's player
 * card, a "Remove {name}?" prompt + warning, and Cancel / Remove actions.
 * Wrapped in a [com.arena_web.ui.components.overlay.DesktopPopup] for both
 * desktop and mobile (centred popup, not a sheet).
 */
@Composable
fun RemoveMemberConfirmContent(
    member: ChatMemberDto,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
) {
    val t = LocalAppStrings.current
    val name = member.fullName?.takeIf { it.isNotBlank() } ?: member.username

    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(22)) }) {
        Column(
            modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(8)) },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(22)) },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Member's player card (≈75×114) or a round avatar fallback.
                val cardFrameUrl = member.cardImageUrl
                if (cardFrameUrl != null) {
                    Box(modifier = Modifier.styleModifier { property("--scale", "1.3px") }) {
                        PlayerCard(
                            playerName = "",
                            rating = 0,
                            position = "",
                            sport = "football",
                            size = PlayerCardSize.XtraSmall,
                            cardFrameUrl = cardFrameUrl,
                            avatarUrl = member.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                            hideRating = true,
                            hideShadow = true,
                        )
                    }
                } else {
                    val initials = name.split(" ").filter { it.isNotBlank() }
                        .take(2).joinToString("") { it.first().uppercase() }.ifBlank { "?" }
                    Box(
                        modifier = Modifier
                            .sSize(80)
                            .sBorderRadius(100)
                            .backgroundColor(Color("#72A5FF")),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            initials,
                            modifier = Modifier.bodyXlMedium().color(Color("#FAFAFB"))
                        )
                    }
                }

                SpanText(
                    t.chatRemoveMemberTitle(name),
                    modifier = Modifier.bodyXlMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            }

            SpanText(
                t.chatRemoveMemberWarning(name),
                modifier = Modifier
                    .fillMaxWidth()
                    .bodyMRegular()
                    .color(Color("#99b1bf"))
                    .textAlign(TextAlign.Center)
                    .styleModifier { property("line-height", s(22)) }
            )
        }

        // Cancel (outline) + Remove (red).
        Row(
            modifier = Modifier.fillMaxWidth().styleModifier {
                property("gap", s(12))
                property("justify-content", "flex-end")
            }
        ) {
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("flex", "1")
                        property("height", s(46))
                        property("border-radius", s(100))
                        property("border", "0.3px solid transparent")
                        property("background", FieldBackground)
                        property("box-sizing", "border-box")
                    }
                    .cursor(Cursor.Pointer)
                    .onClick { onCancel() },
                contentAlignment = Alignment.Center
            ) {
                SpanText(t.commonCancel, modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2))
            }

            Box(
                modifier = Modifier
                    .styleModifier {
                        property("flex", "1")
                        property("height", s(46))
                        property("border-radius", s(100))
                        property("background", "#FF4242")
                    }
                    .cursor(Cursor.Pointer)
                    .onClick { onConfirm() },
                contentAlignment = Alignment.Center
            ) {
                SpanText(t.chatRemoveMemberAction, modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite))
            }
        }
    }
}
